import { useTranslation } from "react-i18next";
import {
  GOOGLE_RATING_STARS,
  type GoogleImportPreview,
  type GoogleImportRequest,
} from "@ckgo2/shared";
import { formatDate } from "@/lib/format";
import { uiLocale } from "@/i18n";

/**
 * Den `YYYY-MM-DD` (datum exportu, `asOf` souhrnu) v jazyce adminu. Holý den je půlnoc UTC
 * a vykresluje se v UTC: v záporném časovém pásmu prohlížeče by jinak vyšel o den dřív.
 * Stejné pravidlo (`timeZone: "UTC"`) platí pro každé datum Google recenzí v adminu.
 */
export function formatExportDay(day: string): string {
  return formatDate(`${day}T00:00:00Z`, { timeZone: "UTC" });
}

/** Známka na jedno desetinné místo v jazyce adminu; `null` (žádná recenze) = pomlčka. */
export function formatGoogleScore(score: number | null, empty: string): string {
  if (score === null) return empty;
  return new Intl.NumberFormat(uiLocale(), {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(score);
}

interface ImportPreviewSummaryProps {
  preview: GoogleImportPreview;
  request: GoogleImportRequest;
  /** `preview` = souhrn před potvrzením (co se stane), `result` = co potvrzení udělalo. */
  mode: "preview" | "result";
}

/**
 * Souhrn importu (M1): hodnoty z odpovědi náhledu nebo potvrzení beze změny, nic se tu
 * nepočítá. Datum exportu a jeho zdroj jsou z téhož požadavku, který šel na server.
 */
export default function ImportPreviewSummary({
  preview,
  request,
  mode,
}: ImportPreviewSummaryProps) {
  const { t } = useTranslation();
  const noValue = t("content.googleReviews.summary.noValue");
  const dateSource =
    request.exportDateSource === "filename"
      ? t("content.googleReviews.summary.exportDateFromFilename")
      : t("content.googleReviews.summary.exportDateFromUpload");
  const isResult = mode === "result";
  const effects: Array<[string, number]> = [
    [
      isResult
        ? t("content.googleReviews.summary.resultAdded")
        : t("content.googleReviews.summary.added"),
      preview.added,
    ],
    [
      isResult
        ? t("content.googleReviews.summary.resultChanged")
        : t("content.googleReviews.summary.changed"),
      preview.changed,
    ],
    [
      isResult
        ? t("content.googleReviews.summary.resultDeleted")
        : t("content.googleReviews.summary.deleted"),
      preview.deleted,
    ],
    [t("content.googleReviews.summary.manualPaired"), preview.manualPaired],
    [t("content.googleReviews.summary.manualAmbiguous"), preview.manualAmbiguous],
    [t("content.googleReviews.summary.skippedRemoved"), preview.skippedRemoved],
  ];

  return (
    <div className="space-y-4">
      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[max-content_1fr]">
        <dt className="text-gray-500 dark:text-gray-400">
          {t("content.googleReviews.summary.location")}
        </dt>
        <dd className="font-medium text-gray-900 dark:text-white">{preview.location.name}</dd>
        <dt className="text-gray-500 dark:text-gray-400">
          {t("content.googleReviews.summary.placeId")}
        </dt>
        <dd className="break-all text-gray-900 dark:text-white">
          {preview.location.placeId ?? t("content.googleReviews.summary.placeIdMissing")}
        </dd>
        <dt className="text-gray-500 dark:text-gray-400">
          {t("content.googleReviews.summary.exportDate")}
        </dt>
        <dd className="text-gray-900 dark:text-white">
          {formatExportDay(request.exportDate)}{" "}
          <span className="text-gray-500 dark:text-gray-400">({dateSource})</span>
        </dd>
        <dt className="text-gray-500 dark:text-gray-400">
          {t("content.googleReviews.summary.total")}
        </dt>
        <dd className="text-gray-900 dark:text-white">{preview.total}</dd>
        <dt className="text-gray-500 dark:text-gray-400">
          {t("content.googleReviews.summary.score")}
        </dt>
        <dd className="text-gray-900 dark:text-white">
          {formatGoogleScore(preview.score, noValue)}
        </dd>
        <dt className="text-gray-500 dark:text-gray-400">
          {t("content.googleReviews.summary.distribution")}
        </dt>
        <dd>
          <ul className="flex flex-wrap gap-x-4 gap-y-1 text-gray-900 dark:text-white">
            {GOOGLE_RATING_STARS.map((stars) => (
              <li key={stars}>
                <span className="text-gray-500 dark:text-gray-400">
                  {t("content.googleReviews.summary.starsRow", { stars })}
                </span>{" "}
                {preview.counts[stars]}
              </li>
            ))}
          </ul>
        </dd>
      </dl>

      <dl className="grid gap-x-6 gap-y-2 rounded-lg bg-gray-50 p-4 text-sm sm:grid-cols-[max-content_1fr] dark:bg-gray-700/40">
        {effects.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-gray-600 dark:text-gray-300">{label}</dt>
            <dd className="font-semibold text-gray-900 dark:text-white">{value}</dd>
          </div>
        ))}
      </dl>

      {!isResult && preview.deleted > 0 && (
        <p className="text-warning-700 dark:text-warning-400 text-sm font-medium">
          {t("content.googleReviews.summary.deletedWarning")}
        </p>
      )}
    </div>
  );
}
